import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import WebCreatorSitePageView from './WebCreatorSitePageView';
import {
  VIEWPORT_LIST,
  EDITOR_Z_INDEX,
  setPreviewScrollRoot,
  detectPublicViewport,
} from '../utils/defaults';

export default function PreviewModal({ site, viewport, onViewportChange, onClose }) {
  const frameRef = useRef(null);
  const [frameViewport, setFrameViewport] = useState('desktop');

  const bindScrollRef = (node) => {
    setPreviewScrollRoot(node);
  };

  useEffect(() => {
    if (viewport === 'tablet' || viewport === 'mobile') {
      setFrameViewport(viewport);
      return undefined;
    }
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      setFrameViewport(detectPublicViewport(window.innerWidth));
      return undefined;
    }
    const measure = () => setFrameViewport(detectPublicViewport(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewport]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      setPreviewScrollRoot(null);
    };
  }, []);

  if (!site) return null;

  const frameWidth =
    viewport === 'desktop' ? '100%' : viewport === 'tablet' ? 768 : 390;
  const renderViewport =
    viewport === 'tablet' || viewport === 'mobile' ? viewport : frameViewport;

  return createPortal(
    <div
      className="fixed inset-0 flex flex-col bg-neutral-900/95"
      style={{ zIndex: EDITOR_Z_INDEX.preview }}
    >
      <header className="shrink-0 flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-white">Aperçu</span>
          <div className="flex rounded-lg overflow-hidden border border-white/20">
            {VIEWPORT_LIST.map((vp) => (
              <button
                key={vp.id}
                type="button"
                className={`px-3 py-1 text-xs ${
                  viewport === vp.id
                    ? 'bg-white text-neutral-900'
                    : 'text-white/80 hover:bg-white/10'
                }`}
                onClick={() => onViewportChange(vp.id)}
              >
                {vp.label}
              </button>
            ))}
          </div>
        </div>
        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={onClose}>
          <Icon name="X" size={16} className="mr-2" />
          Fermer
        </Button>
      </header>

      <div
        ref={bindScrollRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain touch-pan-y"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="flex justify-center p-4 md:p-8 pb-12">
          <div
            ref={frameRef}
            className="bg-white shadow-2xl transition-all duration-300 shrink-0"
            style={{
              width: frameWidth,
              maxWidth: '100%',
            }}
          >
            <WebCreatorSitePageView
              site={site}
              viewport={renderViewport}
              interactive
              checkoutSlug={site.slug}
              onCommerceNavigate={() => {}}
              showBrandingFooter
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
